import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '@components/ui/Header';
import Icon from '@components/AppIcon';
import Button from '@components/ui/Button';
import Footer from '@components/ui/Footer';
import DoctorCard from './components/DoctorCard';
import DoctorProfileModal from './components/DoctorProfileModal';
import SpecialtyFilter from './components/SpecialtyFilter';
import BookingModal from './components/BookingModal';
import PageHeader from './components/PageHeader';

const OurTeamPage = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [bookingDoctor, setBookingDoctor] = useState(null);
  const [teamData, setTeamData] = useState({ doctors: [], why_choose_us: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTeamData = async () => {
      try {
        const response = await fetch('/api/our_team');
        if (!response.ok) {
          throw new Error('Failed to fetch team data');
        }
        const data = await response.json();
        setTeamData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamData();
  }, []);

  const filteredDoctors = activeFilter === 'all' 
  ? teamData.doctors 
  : teamData.doctors.filter(doctor => {
      const specialtyMap = {
        'cataract': ['Cataract', 'Refractive'],
        'lasik': ['LASIK', 'Refractive'],
        'retina': ['Retina', 'Retinal', 'Vitreoretina'],
        'pediatric': ['Pediatric'],
        'glaucoma': ['Glaucoma'],
        'cornea': ['Cornea', 'Corneal'],
        'oculoplastic': ['Oculoplastic']
      };
      
      const keywords = specialtyMap[activeFilter] || [];
      return keywords.some(keyword => 
        doctor.specialty.toLowerCase().includes(keyword.toLowerCase()) || 
        doctor.specializations.some(spec => spec.toLowerCase().includes(keyword.toLowerCase()))
      );
    });

  const handleBookAppointment = (doctor) => {
    setBookingDoctor(doctor);
    setIsBookingModalOpen(true);
  };

  const handleViewProfile = (doctor) => {
    setSelectedDoctor(doctor);
    setIsProfileModalOpen(true);
  };

  const handleBookingConfirm = (bookingData) => {
    // Handle booking confirmation
    console.log('Booking confirmed:', bookingData);
    // You could show a success message or redirect here
  };

   const handleAppointment = () => {
    window.location.href = "/book-appointment-guided-care-journey";
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Our Team - Expert Network & Personal Connections | Eye&U Kenya</title>
        <meta name="description" content="Meet Kenya's most trusted eye care professionals. Our internationally trained specialists combine world-class expertise with genuine compassion and cultural understanding." />
        <meta name="keywords" content="eye doctors Kenya, ophthalmologists Nairobi, eye specialists, cataract surgery, LASIK, retinal specialists, pediatric eye care, glaucoma treatment" />
      </Helmet>

      <Header />

      {/* Team Stats */}
      <PageHeader />

      {/* Specialty Filter */}
      <SpecialtyFilter 
        specialties={teamData.doctors}
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
      />

      {/* Doctors Grid */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-text-primary mb-4" style={{ color: "#08005f" }}>
              {activeFilter === 'all' ? 'Our Complete Medical Team' : `${activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)} Specialists`}
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {filteredDoctors.length} specialist{filteredDoctors.length !== 1 ? 's' : ''} ready to provide you with exceptional eye care
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredDoctors.map((doctor) => (
              <DoctorCard
                key={doctor.id}
                doctor={doctor}
                onBookAppointment={handleBookAppointment}
                onViewProfile={handleViewProfile}
              />
            ))}
          </div>

          {filteredDoctors.length === 0 && (
            <div className="text-center py-16">
              <Icon name="Search" size={48} className="text-text-secondary/50 mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                No specialists found
              </h3>
              <p className="text-text-secondary mb-6">
                Try adjusting your filter or view all specialists
              </p>
              <Button
                variant="outline"
                onClick={() => setActiveFilter('all')}
              >
                View All Specialists
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Our Team */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-[#08005f] mb-4">
              Why Choose Our Medical Team
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Experience the difference that comes from combining international expertise 
              with local understanding and genuine care for every patient.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamData.why_choose_us.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-1 transition"
              >
                <div className="w-14 h-14 bg-[#eb2227]/10 rounded-xl flex items-center justify-center mb-4">
                  <Icon name={feature.icon} size={28} className="text-[#08005f]" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-[#08005f] mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#08005f] mb-14">
  <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
    <h2 className="font-heading font-bold text-3xl lg:text-4xl text-white mb-6">
      Ready to Meet Your Eye Care Specialist?
    </h2>
    <p className="text-white/90 text-lg mb-8">
      Schedule a consultation with one of our expert doctors and take the first step 
      toward better vision and improved quality of life.
    </p>
    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
      <Button
        className="bg-white text-[#08005f] hover:bg-gray-100 font-medium"
        size="lg"
        iconName="Calendar"
        iconPosition="left"
        iconSize={20}
        onClick={handleAppointment}
      >
        Book Appointment
      </Button>
    </div>
  </div>
</section>


      {/* Modals */}
      <DoctorProfileModal
        doctor={selectedDoctor}
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        onBookAppointment={handleBookAppointment}
      />

      <BookingModal
        doctor={bookingDoctor}
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        onBookingConfirm={handleBookingConfirm}
      />

      <Footer />
    </div>
  );
};

export default OurTeamPage;